import { connect } from 'react-redux';
import { pipe } from 'fp-ts/function';
import { formValueSelector, reduxForm } from 'redux-form';
import { Fragment } from 'react';
import { NestedCard, Stack } from '@optimus-web/core';
import { ApplicationState, CommanderThunkDispatch } from '@mews-commander/types';
import { LocalizedText, lt, showSuccessAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { CheckboxField, validation } from '@mews-commander/legacy-form';
import { hasKeyCutters } from '@mews-commander/legacy-entities/src/reducers/keyCutters';
import { getRequirePreauthorizationForStart } from '@mews-commander/legacy-entities/src/ducks/services';
import { cut } from '../../actions';
import preferences from '../../../preferences';
import { KeyCutterFormFields, constraints as keyCutterConstraints } from '../KeyCutterButton/KeyCutterForm';
import { setInspectedAndCheckIn } from './actions';
import AssignedResoureLabel from './AssignedResourceLabel';

const {
    actions: { setPreferredKeyCutterId },
} = preferences;

interface FormValues {
    makeResourceInspected?: boolean;
    preauthorizationCheckDisabled?: boolean;
    createKeys?: boolean;
    keyCount?: number;
    keyCutter: string | null;
}

interface OwnProps {
    form: string;
    isInspected: boolean;
    reservationId: string;
    assignedResourceId: string;
    serviceId: string;
    onSuccess: () => void;
}

interface StateProps {
    isServicePreauthorizationEnabled?: boolean;
    withKeyCutters: boolean;
    showKeyCutter: boolean;
}
interface DispatchProps {
    onSubmit: (values: FormValues) => void;
}

type CheckInFormProps = OwnProps & StateProps & DispatchProps;

const getConstraints = ({ showKeyCutter }: CheckInFormProps) => (showKeyCutter ? keyCutterConstraints : null);

const makeInspectedProps = {
    name: 'makeResourceInspected',
    label: <LocalizedText textKey={TextKey.MakeSpaceInspected} />,
};

const preauthorizationCheckProps = {
    name: 'preauthorizationCheckDisabled',
    label: <LocalizedText textKey={TextKey.PreauthorizationInvalidCheckIn} />,
};

const createKeysProps = {
    name: 'createKeys',
    label: <LocalizedText textKey={TextKey.CreateKeys} />,
};

const CheckInForm = ({
    isInspected,
    assignedResourceId,
    serviceId,
    isServicePreauthorizationEnabled,
    reservationId,
    withKeyCutters,
    showKeyCutter,
}: CheckInFormProps) => {
    const multipleSectionsRendered = isServicePreauthorizationEnabled || withKeyCutters;
    const ResourceStatusWrapper = multipleSectionsRendered ? NestedCard : Fragment;
    return (
        <Stack spacing="200" vertical>
            {isServicePreauthorizationEnabled ? (
                <NestedCard>
                    <CheckboxField {...preauthorizationCheckProps} />
                </NestedCard>
            ) : null}

            <ResourceStatusWrapper>
                <Stack spacing="100" vertical>
                    <LocalizedText
                        textKey={
                            isInspected
                                ? TextKey.ResourceReadyForCheckInMessage
                                : TextKey.InspectResourceBeforeCheckInMessage
                        }
                    />
                    <AssignedResoureLabel resourceId={assignedResourceId} serviceId={serviceId} />
                    {!isInspected ? <CheckboxField {...makeInspectedProps} /> : null}
                </Stack>
            </ResourceStatusWrapper>

            {withKeyCutters ? (
                <NestedCard>
                    <CheckboxField {...createKeysProps} />
                    {showKeyCutter && <KeyCutterFormFields reservationIds={[reservationId]} showKeyCutterCount />}
                </NestedCard>
            ) : null}
        </Stack>
    );
};

export default pipe(
    CheckInForm,
    reduxForm({ enableReinitialize: true, validate: validation(getConstraints) }),
    connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
        (state, { serviceId, form }) => ({
            isServicePreauthorizationEnabled: getRequirePreauthorizationForStart(state, {
                id: serviceId,
            }),
            withKeyCutters: hasKeyCutters(state),
            showKeyCutter: formValueSelector(form)(state, 'createKeys'),
        }),
        (dispatch: CommanderThunkDispatch, { reservationId, onSuccess }) => ({
            onSubmit: async ({
                makeResourceInspected,
                preauthorizationCheckDisabled,
                createKeys,
                keyCutter,
                keyCount,
            }: FormValues) => {
                await dispatch(
                    setInspectedAndCheckIn({
                        reservationId,
                        disablePreauthorization: preauthorizationCheckDisabled,
                        makeResourceInspected,
                    })
                );
                if (createKeys && keyCutter !== null) {
                    await dispatch(cut(keyCutter, [reservationId], keyCount));
                    dispatch(setPreferredKeyCutterId(keyCutter));
                }
                showSuccessAlert({ description: lt('ReservationCheckInSuccessMessage') });
                return onSuccess();
            },
        })
    )
);
